import { useCallback, useEffect, useRef, useState } from 'react';

const activeCloseRef: { current: (() => void) | null } = { current: null };

const useTooltipState = ({ isMobile }: { isMobile?: boolean } = {}) => {
  const [isOpen, setIsOpen] = useState(false);
  const showTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const hideTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const clearTimers = () => {
    clearTimeout(showTimer.current);
    clearTimeout(hideTimer.current);
  };

  useEffect(() => () => clearTimers(), []);

  const close = useCallback(() => {
    clearTimers();
    setIsOpen(false);
  }, []);

  const open = useCallback(() => {
    clearTimers();
    if (activeCloseRef.current && activeCloseRef.current !== close) activeCloseRef.current();
    activeCloseRef.current = close;
    setIsOpen(true);
  }, [close]);

  const toggle = useCallback(() => {
    if (isOpen) close();
    else open();
  }, [isOpen, open, close]);

  useEffect(() => {
    if (!isOpen) return undefined;
    activeCloseRef.current = close;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onEsc);
    const onScroll = isMobile ? () => close() : null;
    if (onScroll) window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('keydown', onEsc);
      if (onScroll) window.removeEventListener('scroll', onScroll, true);
      if (activeCloseRef.current === close) activeCloseRef.current = null;
    };
  }, [isOpen, isMobile, close]);

  const scheduleOpen = useCallback(() => {
    clearTimers();
    showTimer.current = setTimeout(open, 300);
  }, [open]);

  const scheduleClose = useCallback(() => {
    clearTimers();
    hideTimer.current = setTimeout(close, 1000);
  }, [close]);

  return { isOpen, open, close, toggle, scheduleOpen, scheduleClose };
};

export default useTooltipState;
